import './styles.css';
import { TankzoneApp } from './ui/app';
import type { KeyboardSetting, ReducedMotionSetting, TouchMode } from './storage/storage';

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element not found: ${selector}`);
  return element;
}

const canvas = requiredElement<HTMLCanvasElement>('#game-canvas');
const overlay = requiredElement<HTMLElement>('#game-overlay');
const startButton = requiredElement<HTMLButtonElement>('#start-button');
const gameFrame = requiredElement<HTMLElement>('#game-frame');
const helpButton = requiredElement<HTMLButtonElement>('#help-button');
const settingsButton = requiredElement<HTMLButtonElement>('#settings-button');
const muteButton = requiredElement<HTMLButtonElement>('#mute-button');
const liveRegion = requiredElement<HTMLElement>('#live-region');
const leftLever = requiredElement<HTMLButtonElement>('#left-lever');
const rightLever = requiredElement<HTMLButtonElement>('#right-lever');
const touchFire = requiredElement<HTMLButtonElement>('#touch-fire');
const touchPause = requiredElement<HTMLButtonElement>('#touch-pause');
const touchMute = requiredElement<HTMLButtonElement>('#touch-mute');
const pauseButton = requiredElement<HTMLButtonElement>('#pause-button');
const primaryPanel = requiredElement<HTMLElement>('#primary-panel');
const initialsPanel = requiredElement<HTMLFormElement>('#initials-panel');
const initialsDialog = requiredElement<HTMLDialogElement>('#initials-dialog');
const initialsInput = requiredElement<HTMLInputElement>('#initials-input');
const characterGrid = requiredElement<HTMLElement>('#character-grid');
const initialsBackspace = requiredElement<HTMLButtonElement>('#initials-backspace');
const helpDialog = requiredElement<HTMLDialogElement>('#help-dialog');
const settingsDialog = requiredElement<HTMLDialogElement>('#settings-dialog');
const keyboardSetting = requiredElement<HTMLSelectElement>('#keyboard-setting');
const touchSetting = requiredElement<HTMLSelectElement>('#touch-setting');
const motionSetting = requiredElement<HTMLSelectElement>('#motion-setting');
const volumeSetting = requiredElement<HTMLInputElement>('#volume-setting');
const clearScores = requiredElement<HTMLButtonElement>('#clear-scores');

const app = new TankzoneApp(canvas, overlay, leftLever, rightLever, touchFire);
app.mount();

const syncSettingsUi = (): void => {
  const settings = app.getSettings();
  keyboardSetting.value = settings.keyboardPreset;
  touchSetting.value = settings.touchMode;
  motionSetting.value = settings.reducedMotion;
  volumeSetting.value = String(settings.volume);
  muteButton.setAttribute('aria-pressed', String(settings.muted));
  muteButton.textContent = settings.muted ? 'SOUND: MUTED' : 'SOUND: ARMED';
  touchMute.setAttribute('aria-pressed', String(settings.muted));
  touchMute.textContent = settings.muted ? 'UNMUTE' : 'MUTE';
};

syncSettingsUi();

for (const character of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-') {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = character;
  button.autofocus = character === 'A';
  button.setAttribute('aria-label', `Add ${character}`);
  button.addEventListener('click', () => app.appendInitial(character));
  characterGrid.append(button);
}

if (import.meta.env.VITE_TEST_MODE === 'true') {
  window.__TANKZONE_TEST__ = {
    snapshot: () => app.testSnapshot(),
    command: (name, value) => app.testCommand(name, value),
    measureFrameP95,
  };
}

const start = (): void => {
  app.start();
};

startButton.addEventListener('click', start);
document.addEventListener('keydown', (event) => {
  const editingControl = event.target instanceof Element &&
    event.target.closest('input, select, textarea, [contenteditable="true"]') !== null;
  const interactiveControl = event.target instanceof Element &&
    event.target.closest('button, input, select, textarea, a[href], [contenteditable="true"]') !== null;
  if (app.isEnteringInitials()) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      app.backspaceInitial();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      app.submitInitials();
    } else if (event.target !== initialsInput && /^[A-Z0-9.-]$/i.test(event.key)) {
      event.preventDefault();
      app.appendInitial(event.key);
    }
    return;
  }
  if (
    (event.code === 'KeyM' || event.key.toLowerCase() === 'm')
    && !editingControl
    && !event.ctrlKey
    && !event.metaKey
    && !event.altKey
  ) {
    event.preventDefault();
    updateMute();
    return;
  }
  if (
    event.key === 'Enter'
    && !interactiveControl
    && !helpDialog.open
    && !settingsDialog.open
    && !primaryPanel.hidden
    && !overlay.classList.contains('game-overlay--hidden')
  ) {
    event.preventDefault();
    start();
    event.stopPropagation();
  }
});

const updateMute = (): void => {
  const muted = app.toggleMuted();
  muteButton.setAttribute('aria-pressed', String(muted));
  muteButton.textContent = muted ? 'SOUND: MUTED' : 'SOUND: ARMED';
  touchMute.setAttribute('aria-pressed', String(muted));
  touchMute.textContent = muted ? 'UNMUTE' : 'MUTE';
  liveRegion.textContent = muted ? 'Sound muted.' : 'Sound armed.';
};

muteButton.addEventListener('click', updateMute);
touchMute.addEventListener('click', updateMute);
touchPause.addEventListener('click', () => app.togglePause());
pauseButton.addEventListener('click', () => app.togglePause());

const openDialog = (dialog: HTMLDialogElement): void => {
  app.pauseForDialog();
  if (!dialog.open) dialog.showModal();
};

helpButton.addEventListener('click', () => openDialog(helpDialog));
settingsButton.addEventListener('click', () => {
  syncSettingsUi();
  openDialog(settingsDialog);
});

helpDialog.addEventListener('close', () => {
  (app.isPaused() ? startButton : helpButton).focus();
});
settingsDialog.addEventListener('close', () => {
  syncSettingsUi();
  (app.isPaused() ? startButton : settingsButton).focus();
});

keyboardSetting.addEventListener('change', () => {
  app.updateSettings({ keyboardPreset: keyboardSetting.value as KeyboardSetting });
});
touchSetting.addEventListener('change', () => {
  app.updateSettings({ touchMode: touchSetting.value as TouchMode });
});
motionSetting.addEventListener('change', () => {
  app.updateSettings({ reducedMotion: motionSetting.value as ReducedMotionSetting });
});
volumeSetting.addEventListener('input', () => {
  app.updateSettings({ volume: Number(volumeSetting.value) });
});
clearScores.addEventListener('click', () => {
  if (window.confirm('Restore the built-in local score table?')) app.clearScores();
});

initialsInput.addEventListener('input', () => app.setInitials(initialsInput.value));
initialsBackspace.addEventListener('click', () => app.backspaceInitial());
initialsPanel.addEventListener('submit', (event) => {
  event.preventDefault();
  app.submitInitials();
});
initialsDialog.addEventListener('cancel', (event) => event.preventDefault());

gameFrame.addEventListener('pointerdown', (event) => {
  if (event.target === gameFrame || event.target === canvas) gameFrame.focus();
});
window.addEventListener('pagehide', () => app.destroy(), { once: true });

function measureFrameP95(requestedFrames = 300): Promise<number> {
  const frameCount = Math.max(30, Math.min(600, Math.floor(requestedFrames)));
  return new Promise((resolve) => {
    const intervals: number[] = [];
    let previous = 0;
    const sample = (time: number): void => {
      if (previous !== 0) intervals.push(time - previous);
      previous = time;
      if (intervals.length < frameCount) requestAnimationFrame(sample);
      else {
        intervals.sort((left, right) => left - right);
        resolve(intervals[Math.ceil(intervals.length * 0.95) - 1] ?? Number.POSITIVE_INFINITY);
      }
    };
    requestAnimationFrame(sample);
  });
}
